// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

// Component imports
import { Button, Input, Row, Col, Form, Radio, Checkbox } from 'antd';
import { RiDeleteBin6Line } from 'react-icons/ri';

// Misc imports
import {
  BINARY_ANSWERS, ENTITY_TYPES, ENVIRONMENTS, FELT_JUDGED,
  PREVIEW_MODES, REALITY_PERCEPTIONS, STORAGE_KEYS, TERNARY_ANSWERS, TIME_PERCEPTIONS
} from '../../constants';

const AdvancedInformationTab = props => {
  const { wrapperCol, mode, onSubmitSuccess, onStepChange, initialValues, onDelete } = props;
  const location = useLocation();
  const { pathname } = location;
  const [form] = Form.useForm();
  const [, updateState] = useState(); // Hook to force a component re-render

  // TODO: For fields that appear and disappear, make sure to clear the values if needed

  // On mount
  useEffect(() => {
    form.resetFields();
    updateState({}); // Trigger re-render so conditional fields show based on initial values

    return () => {
      onStepChange(form.getFieldsValue(true));
    };
  }, [pathname]);

  return (
    <Form
      layout="vertical"
      name="advancedInformationForm"
      form={form}
      initialValues={initialValues || JSON.parse(window.localStorage.getItem(STORAGE_KEYS.NDE_TO_CREATE)) || {}}
      onFinish={(values) => onSubmitSuccess(values)} // Let parent form group handle saving based on edit/create mode
      onFieldsChange={(_, allFields) => updateState(allFields)}
      scrollToFirstError
    >
      <Row>
        <Col {...wrapperCol}>
          <Row>
            <Col lg={18} span={24} className="mb-32">
              <h3>Advanced Information</h3>
              <p className="p1-body mb-0">
                Here you can enter more details about your experience. Most questions will be based on the <a href="https://en.wikipedia.org/wiki/Bruce_Greyson" target="_blank" rel="noreferrer">Greyson Scale</a>, a widely used scale to measure the aspects of near-death experiences.
              </p>
            </Col>

            <Col span={24} className="mt-4">
              <Row className="w-100">
                <Col span={24} className="mb-24">
                  <h5 className="mb-20">
                    Did time appear to go faster or slower?
                  </h5>
                  <Form.Item name="timePerception">
                    <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                      <Radio value={TIME_PERCEPTIONS.FASTER}>Faster</Radio>
                      <Radio value={TIME_PERCEPTIONS.SLOWER}>Slower</Radio>
                      <Radio value={TIME_PERCEPTIONS.UNCHANGED}>No change</Radio>
                      <Radio value={TIME_PERCEPTIONS.NO_TIME}>There was no time</Radio>
                      <Radio value={TIME_PERCEPTIONS.OTHER}>Other</Radio>
                    </Radio.Group>
                  </Form.Item>

                  {
                    form.getFieldValue('timePerception') === TIME_PERCEPTIONS.FASTER || form.getFieldValue('timePerception') === TIME_PERCEPTIONS.SLOWER
                    || form.getFieldValue('timePerception') === TIME_PERCEPTIONS.NO_TIME || form.getFieldValue('timePerception') === TIME_PERCEPTIONS.OTHER ? (
                        <Form.Item name="timePerceptionText">
                          <Input size="large" placeholder="Please describe (optional)" />
                        </Form.Item>
                      ) : null
                  }
                </Col>

                <Col span={24} className="mb-24">
                  <h5 className="mb-20">
                    Did you become separated from your body?
                  </h5>

                  <Form.Item name="hadObe">
                    <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                      <Radio value={TERNARY_ANSWERS.YES}>Yes</Radio>
                      <Radio value={TERNARY_ANSWERS.NO}>No</Radio>
                      <Radio value={TERNARY_ANSWERS.OTHER}>Other</Radio>
                    </Radio.Group>
                  </Form.Item>

                  {
                    form.getFieldValue('hadObe') === TERNARY_ANSWERS.YES || form.getFieldValue('hadObe') === TERNARY_ANSWERS.OTHER ? (
                      <Form.Item name="hadObeText">
                        <Input size="large" placeholder="Please describe (optional)" />
                      </Form.Item>
                    ) : null
                  }
                </Col>

                <Col span={24} className="mb-24">
                  <h5 className="mb-4">
                    Did you see your past or had a life review?
                  </h5>
                  <p className="p1-body mb-20">
                    It is common among (usually positive) NDEs to go through a "life review", where one reviews parts of their life and the effects it had on others.
                  </p>

                  <Form.Item name="hadLifeReview">
                    <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                      <Radio value={TERNARY_ANSWERS.YES}>Yes</Radio>
                      <Radio value={TERNARY_ANSWERS.NO}>No</Radio>
                      <Radio value={TERNARY_ANSWERS.OTHER}>Other</Radio>
                    </Radio.Group>
                  </Form.Item>

                  {
                    form.getFieldValue('hadLifeReview') === TERNARY_ANSWERS.YES || form.getFieldValue('hadLifeReview') === TERNARY_ANSWERS.OTHER ? (
                      <Form.Item name="hadLifeReviewText">
                        <Input size="large" placeholder="Please describe (optional)" />
                      </Form.Item>
                    ) : null
                  }
                </Col>

                <Col span={24} className="mb-24">
                  <h5 className="mb-20">
                    Did your experience feel like a dream?
                  </h5>

                  <Form.Item name="realityPerception">
                    <Radio.Group className="radio-group-cards d-block">
                      <Row gutter={[12, 12]}>
                        <Col xs={24} md={12}>
                          <Radio value={REALITY_PERCEPTIONS.DREAMLIKE} className="w-100">Felt like a dream</Radio>
                        </Col>
                        <Col xs={24} md={12}>
                          <Radio value={REALITY_PERCEPTIONS.LIFELIKE} className="w-100">Felt just like life</Radio>
                        </Col>
                        <Col xs={24} md={12}>
                          <Radio value={REALITY_PERCEPTIONS.MORE_REAL_THAN_LIFE} className="w-100">Felt more real than life</Radio>
                        </Col>
                        <Col xs={24} md={12}>
                          <Radio value={REALITY_PERCEPTIONS.OTHER} className="w-100">Other</Radio>
                        </Col>
                      </Row>
                    </Radio.Group>
                  </Form.Item>

                  {
                    form.getFieldValue('realityPerception') === REALITY_PERCEPTIONS.MORE_REAL_THAN_LIFE || form.getFieldValue('realityPerception') === REALITY_PERCEPTIONS.OTHER ? (
                      <Form.Item name="realityPerceptionText">
                        <Input size="large" placeholder="Please describe (optional)" />
                      </Form.Item>
                    ) : null
                  }
                </Col>

                <Col span={24} className="mb-24">
                  <h5 className="mb-20">
                    Did you feel a sense of unity with everything?
                  </h5>

                  <Form.Item name="feltUnity">
                    <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                      <Radio value={TERNARY_ANSWERS.YES}>Yes</Radio>
                      <Radio value={TERNARY_ANSWERS.NO}>No</Radio>
                      <Radio value={TERNARY_ANSWERS.OTHER}>Other</Radio>
                    </Radio.Group>
                  </Form.Item>

                  {
                    form.getFieldValue('feltUnity') === TERNARY_ANSWERS.OTHER ? (
                      <Form.Item name="feltUnityText">
                        <Input size="large" placeholder="Please describe (optional)" />
                      </Form.Item>
                    ) : null
                  }
                </Col>

                <Col span={24} className="mb-24">
                  <h5 className="mb-4">
                    Did you see a bright light?
                  </h5>
                  <p className="p1-body mb-20">
                    It is common in (usually positive) NDEs to see a "loving light", filled with warmth and unconditional love.
                  </p>

                  <Form.Item name="sawBrightLight">
                    <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                      <Radio value={true}>Yes</Radio>
                      <Radio value={false}>No</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>

                <Col span={24} className="mb-24">
                  <h5 className="mb-20">
                    Did you feel a sense of understanding everything about life or the world?
                  </h5>

                  <Form.Item name="feltSenseOfUnderstanding">
                    <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                      <Radio value={TERNARY_ANSWERS.YES}>Yes</Radio>
                      <Radio value={TERNARY_ANSWERS.NO}>No</Radio>
                      <Radio value={TERNARY_ANSWERS.OTHER}>Other</Radio>
                    </Radio.Group>
                  </Form.Item>

                  {
                    form.getFieldValue('feltSenseOfUnderstanding') === TERNARY_ANSWERS.YES || form.getFieldValue('feltSenseOfUnderstanding') === TERNARY_ANSWERS.OTHER ? (
                      <Form.Item name="feltSenseOfUnderstandingText">
                        <Input size="large" placeholder="Please describe (optional)" />
                      </Form.Item>
                    ) : null
                  }
                </Col>

                <Col span={24} className="mb-24">
                  <h5 className="mb-4">
                    Did you see or feel another presence with you?
                  </h5>
                  <p className="p1-body mb-20">
                    Answer with "yes" if at any point of your experience you felt you were not alone.
                  </p>

                  <Form.Item name="feltOtherEntities">
                    <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                      <Radio value={BINARY_ANSWERS.YES}>Yes</Radio>
                      <Radio value={BINARY_ANSWERS.NO}>No</Radio>
                    </Radio.Group>
                  </Form.Item>

                  {
                    form.getFieldValue('feltOtherEntities') === BINARY_ANSWERS.YES ? (
                      <Form.Item name="feltOtherEntitiesText">
                        <Input size="large" placeholder="Please describe (optional)" />
                      </Form.Item>
                    ) : null
                  }
                </Col>

                {
                  form.getFieldValue('feltOtherEntities') === BINARY_ANSWERS.YES ? (
                    <Col span={24} className="mb-24">
                      <h5 className="mb-4">
                        What kind of other entities did you see?
                      </h5>
                      <p className="p1-body mb-20">
                        Choose all that apply.
                      </p>

                      <Form.Item name="entityTypes">
                        <Checkbox.Group
                          className="checkbox-group-cards checkbox-group-cards-stretch"
                          options={[
                            { label: 'Loved ones that passed on', value: ENTITY_TYPES.LOVED_ONES_DEAD },
                            { label: 'Loved ones that were still alive', value: ENTITY_TYPES.LOVED_ONES_ALIVE },
                            { label: 'Benevolent spirits', value: ENTITY_TYPES.SPIRITS_BENEVOLENT },
                            { label: 'Malevolent spirits', value: ENTITY_TYPES.SPIRITS_MALEVOLENT },
                            { label: 'Pets', value: ENTITY_TYPES.PETS },
                            { label: 'Non-pet animals', value: ENTITY_TYPES.ANIMALS }
                          ]}
                        >
                        </Checkbox.Group>
                      </Form.Item>
                    </Col>
                  ) : null
                }

                <Col span={24} className="mb-24">
                  <h5 className="mb-20">
                    Did you have any visions or information of events that were yet to happen?
                  </h5>

                  <Form.Item name="hadVisionsOfFuture">
                    <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                      <Radio value={TERNARY_ANSWERS.YES}>Yes</Radio>
                      <Radio value={TERNARY_ANSWERS.NO}>No</Radio>
                      <Radio value={TERNARY_ANSWERS.OTHER}>Other</Radio>
                    </Radio.Group>
                  </Form.Item>

                  {
                    form.getFieldValue('hadVisionsOfFuture') === TERNARY_ANSWERS.YES || form.getFieldValue('hadVisionsOfFuture') === TERNARY_ANSWERS.OTHER ? (
                      <Form.Item name="hadVisionsOfFutureText">
                        <Input size="large" placeholder="Please describe (optional)" />
                      </Form.Item>
                    ) : null
                  }
                </Col>

                {
                  form.getFieldValue('hadVisionsOfFuture') === TERNARY_ANSWERS.YES ? (
                    <Col span={24} className="mb-24">
                      <h5 className="mb-20">
                        Did your future visions come true?
                      </h5>

                      <Form.Item name="visionsCameTrue">
                        <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                          <Radio value={TERNARY_ANSWERS.YES}>Yes</Radio>
                          <Radio value={TERNARY_ANSWERS.NO}>No</Radio>
                          <Radio value={TERNARY_ANSWERS.OTHER}>Not yet / Other</Radio>
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                  ) : null
                }
              </Row>
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                Did you feel judged at any moment during your experience?
              </h5>
              <Form.Item name="feltJudged">
                <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                  <Radio value={FELT_JUDGED.YES_SELF}>Yes, by myself</Radio>
                  <Radio value={FELT_JUDGED.YES_OTHER}>Yes, by someone else</Radio>
                  <Radio value={FELT_JUDGED.NO}>No</Radio>
                </Radio.Group>
              </Form.Item>

              {
                form.getFieldValue('feltJudged') === FELT_JUDGED.YES_OTHER || form.getFieldValue('feltJudged') === FELT_JUDGED.YES_SELF ? (
                  <Form.Item name="feltJudgedText">
                    <Input size="large" placeholder="Please describe (optional)" />
                  </Form.Item>
                ) : null
              }
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                Were you given a choice to return to life?
              </h5>

              <Form.Item name="hadChoiceToReturn">
                <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                  <Radio value={BINARY_ANSWERS.YES}>Yes</Radio>
                  <Radio value={BINARY_ANSWERS.NO}>No</Radio>
                </Radio.Group>
              </Form.Item>

              {
                form.getFieldValue('hadChoiceToReturn') === BINARY_ANSWERS.YES ? (
                  <Form.Item name="hadChoiceToReturnText">
                    <Input size="large" placeholder="Please describe (optional)" />
                  </Form.Item>
                ) : null
              }
            </Col>

            {
              // TODO:
              form.getFieldValue('environment') && form.getFieldValue('environment').includes(ENVIRONMENTS.PLACE_OF_SUFFERING) ? (
                <Fragment>
                  <Col span={24} className="mb-24">
                    <h5 className="mb-4">
                      Did you manage to get out of the place of suffering? If yes, how?
                    </h5>
                    <p className="p1-body mb-20">
                      Some negative NDEs might have mixed elements, with patients eventually managing to escape the suffering and ending up in a place of love. However, nedative NDEs have a lot of variety compared to positive ones.
                    </p>

                    <Form.Item name="escapedNegativeNde">
                      <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                        <Radio value={BINARY_ANSWERS.YES}>Yes</Radio>
                        <Radio value={BINARY_ANSWERS.NO}>No</Radio>
                      </Radio.Group>
                    </Form.Item>

                    {
                      form.getFieldValue('escapedNegativeNde') === BINARY_ANSWERS.YES ? (
                        <Form.Item name="escapedNegativeNdeText">
                          <Input size="large" placeholder="Please describe (optional)" />
                        </Form.Item>
                      ) : null
                    }
                  </Col>

                  <Col span={24} className="mb-24">
                    <h5 className="mb-20">
                      What was your mental state like before you died? Did you feel you deserved to have a negative experience?
                    </h5>

                    <Form.Item name="feltDeservedNegativeNde">
                      <Radio.Group className="radio-group-cards d-block">
                        <Row gutter={[12, 12]}>
                          <Col xs={24} md={12}>
                            <Radio value={BINARY_ANSWERS.YES} className="w-100">Yes</Radio>
                          </Col>
                          <Col xs={24} md={12}>
                            <Radio value={BINARY_ANSWERS.NO} className="w-100">No</Radio>
                          </Col>
                        </Row>
                      </Radio.Group>
                    </Form.Item>

                    <Form.Item name="feltDeservedNegativeNdeText">
                      <Input size="large" placeholder="Please describe (optional)" />
                    </Form.Item>
                  </Col>
                </Fragment>
              ) : null
            }

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                Thanks for taking the time to fill the form! One last question. How did your experience affect your life?
              </h5>

              <Form.Item name="ndeEffects">
                <Input.TextArea size="large" rows={6} placeholder="Please describe (optional)" />
              </Form.Item>
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                What emotions did you feel during your experience?
              </h5>

              <Form.Item name="emotions">
                <Input size="large" placeholder="e.g. peace, joy, love, fear (optional)" />
              </Form.Item>
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-4">
                Was your experience shared simultaneously with another person?
              </h5>
              <p className="p1-body mb-20">
                A shared NDE is when two or more people have the same near-death experience at the same time.
              </p>

              <Form.Item name="hadSharedExperience">
                <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>

              {
                form.getFieldValue('hadSharedExperience') === true ? (
                  <Form.Item name="hadSharedExperienceText">
                    <Input size="large" placeholder="Please describe (optional)" />
                  </Form.Item>
                ) : null
              }
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-4">
                Did you develop any psychic or paranormal abilities after your experience?
              </h5>

              <Form.Item name="developedPsychicAbilities">
                <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>

              {
                form.getFieldValue('developedPsychicAbilities') === true ? (
                  <Form.Item name="developedPsychicAbilitiesText">
                    <Input size="large" placeholder="Please describe (optional)" />
                  </Form.Item>
                ) : null
              }
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                Did you see or pass through a tunnel?
              </h5>

              <Form.Item name="sawTunnel">
                <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>

              {
                form.getFieldValue('sawTunnel') === true ? (
                  <Form.Item name="sawTunnelText">
                    <Input size="large" placeholder="Please describe (optional)" />
                  </Form.Item>
                ) : null
              }
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                Did you hear music or other sounds during your experience?
              </h5>

              <Form.Item name="heardMusic">
                <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>

              {
                form.getFieldValue('heardMusic') === true ? (
                  <Form.Item name="heardMusicText">
                    <Input size="large" placeholder="Please describe (optional)" />
                  </Form.Item>
                ) : null
              }
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                Did you see animals during your experience?
              </h5>

              <Form.Item name="sawAnimals">
                <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>

              {
                form.getFieldValue('sawAnimals') === true ? (
                  <Form.Item name="sawAnimalsText">
                    <Input size="large" placeholder="Please describe (optional)" />
                  </Form.Item>
                ) : null
              }
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-4">
                Did you not want to return to your body or life?
              </h5>

              <Form.Item name="didNotWantToReturn">
                <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>

              {
                form.getFieldValue('didNotWantToReturn') === true ? (
                  <Form.Item name="didNotWantToReturnText">
                    <Input size="large" placeholder="Please describe (optional)" />
                  </Form.Item>
                ) : null
              }
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-4">
                Did you lose your fear of death after this experience?
              </h5>

              <Form.Item name="lostFearOfDeath">
                <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>

              {
                form.getFieldValue('lostFearOfDeath') === true ? (
                  <Form.Item name="lostFearOfDeathText">
                    <Input size="large" placeholder="Please describe (optional)" />
                  </Form.Item>
                ) : null
              }
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                Have you shared this experience publicly (e.g., with media, online)?
              </h5>

              <Form.Item name="sharedExperiencePublicly">
                <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>

            <Col span={24} className="mb-24">
              <h5 className="mb-20">
                Had you had a prior near-death experience before this one?
              </h5>

              <Form.Item name="hadPriorNde">
                <Radio.Group className="radio-group-cards radio-group-cards-stretch">
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      {
        mode === PREVIEW_MODES.VIEW ? null : (
          <Row justify={mode === PREVIEW_MODES.EDIT ? 'space-between' : 'end'} align="middle" className="mt-48">
            {
              mode === PREVIEW_MODES.EDIT ? (
                <Col>
                  <Button
                    danger
                    icon={<RiDeleteBin6Line size={14} style={{ marginRight: 4 }} />}
                    onClick={onDelete}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    Delete NDE
                  </Button>
                </Col>
              ) : null
            }
            <Col>
              <Button type="primary" htmlType="submit" className="bg-black-100 border-color-black-100 hover-bg-black-80 hover-border-color-black-80">
                {
                  mode === PREVIEW_MODES.EDIT ? 'Save' : 'Next step'
                }
              </Button>
            </Col>
          </Row>
        )
      }
    </Form>
  );
};

AdvancedInformationTab.propTypes = {
  mode: PropTypes.string,
  wrapperCol: PropTypes.object,
  onSubmitSuccess: PropTypes.func,
  onStepChange: PropTypes.func,
  initialValues: PropTypes.object,
  onDelete: PropTypes.func
};

export default AdvancedInformationTab;
